import { useNeighbourAlarms } from '@hakimo-ui/hakimo/data-access';
import { NeighbourAlarmsResponse } from '@hakimo-ui/hakimo/types';
import { useUser } from '@hakimo-ui/hakimo/util';
import { HakimoSpinner, Tooltip } from '@hakimo-ui/shared/ui-base';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
} from '../alarm-provider/AlarmGroupProvider';
import { useAlarmGroupTracking } from '../useAlarmGroupTracking';
import { getNeighborAlarmsSearchParams } from './utils';

interface Props {
  disabled?: boolean;
}

export function NeighbourAlarmsActions(props: Props) {
  const { disabled = false } = props;
  const user = useUser();

  const { alarmCameraIds, location, product, isScanLike, dataVersion } =
    useAlarmGroupStatic();
  const track = useAlarmGroupTracking();
  const {
    timelineBounds: { totalTimelineStartTime, totalTimelineEndTime },
    handleNeighbourAlarmsResponse,
  } = useAlarmGroupPlayback();

  const [isNeighbourAlarmsLoading, setIsNeighbourAlarmsLoading] =
    useState(false);
  const [actionClicked, setActionClicked] = useState<'next' | 'prev'>();

  const onNeighbourAlarmsResponse = (data: NeighbourAlarmsResponse) => {
    setIsNeighbourAlarmsLoading(false);
    handleNeighbourAlarmsResponse(data);
  };

  const fetchNeighbourAlarmsApi = useNeighbourAlarms(
    onNeighbourAlarmsResponse,
    dataVersion
  );

  const fetchNeighbourAlarms = useCallback(
    (seconds: number) => () => {
      track('alarm_group_load_neighbour_alarms', {
        direction: seconds > 0 ? 'next' : 'prev',
      });
      let startTimestamp = 0;
      let endTimestamp = 0;
      if (seconds > 0) {
        startTimestamp = totalTimelineEndTime + 1000;
        endTimestamp = totalTimelineEndTime + seconds * 1000;
        setActionClicked('next');
      } else {
        startTimestamp =
          totalTimelineStartTime - Math.abs(seconds) * 1000 - 1000;
        endTimestamp = totalTimelineStartTime - 1000;
        setActionClicked('prev');
      }

      const searchParams = getNeighborAlarmsSearchParams(
        startTimestamp,
        endTimestamp,
        alarmCameraIds,
        location?.id,
        isScanLike ? undefined : 'All',
        user.email,
        product
      );
      setIsNeighbourAlarmsLoading(true);
      fetchNeighbourAlarmsApi(searchParams);
    },
    [
      totalTimelineStartTime,
      totalTimelineEndTime,
      product,
      isScanLike,
      alarmCameraIds,
      track,
    ]
  );

  return (
    <div className="flex justify-between gap-2">
      <Tooltip text="Prev 1 minute alarms" position="top" size="medium">
        <div
          tabIndex={1}
          className={clsx(
            'border-onlight-line-2 dark:border-ondark-line-2 hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3 flex cursor-pointer items-center gap-px rounded-full border p-[2px] px-1 text-[10px]',
            (disabled ||
              (isNeighbourAlarmsLoading && actionClicked === 'prev')) &&
              'pointer-events-none relative opacity-50'
          )}
          onClick={fetchNeighbourAlarms(-60)}
        >
          <ChevronLeftIcon className="h-3 w-3" />
          <span>-1m</span>
          {isNeighbourAlarmsLoading && actionClicked === 'prev' && (
            <span className="absolute inset-0 inline-flex items-center justify-center bg-inherit">
              <HakimoSpinner />
            </span>
          )}
        </div>
      </Tooltip>
      <Tooltip text="Next 1 minute alarms" position="top" size="medium">
        <div
          tabIndex={1}
          className={clsx(
            'border-onlight-line-2 dark:border-ondark-line-2 hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3 flex cursor-pointer items-center gap-px rounded-full border p-[2px] px-1 text-[10px]',
            (disabled ||
              (isNeighbourAlarmsLoading && actionClicked === 'next')) &&
              'pointer-events-none relative opacity-50'
          )}
          onClick={fetchNeighbourAlarms(60)}
        >
          <span>+1m</span>
          <ChevronRightIcon className="h-3 w-3" />
          {isNeighbourAlarmsLoading && actionClicked === 'next' && (
            <span className="absolute inset-0 inline-flex items-center justify-center bg-inherit">
              <HakimoSpinner />
            </span>
          )}
        </div>
      </Tooltip>
    </div>
  );
}

export default NeighbourAlarmsActions;
